import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ErrorBoundary from '../../components/ErrorBoundary';
import { supabase } from '../../lib/supabase';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Input,
  Stack,
  VStack,
  Text,
  Heading,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { AuthError } from '@supabase/supabase-js';

// Custom error fallback
const PasswordUpdateErrorFallback = ({
  error,
  onReset,
}: {
  error: Error | null;
  onReset: () => void;
}) => {
  const navigate = useNavigate();

  return (
    <Container maxW="container.sm" py={10}>
      <Alert status="error" borderRadius="md" mb={6}>
        <AlertIcon />
        <Box flex="1">
          <AlertTitle>Password Update Error</AlertTitle>
          <AlertDescription>
            We're having trouble with the password update service.
          </AlertDescription>
        </Box>
      </Alert>
      <VStack spacing={4}>
        <Button colorScheme="blue" onClick={() => window.location.reload()}>
          Reload Page
        </Button>
        <Button variant="outline" onClick={() => navigate('/login')}>
          Back to Login
        </Button>
      </VStack>
    </Container>
  );
};

// Main component
function UpdatePasswordPageContent() {
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState('');
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  // Get email from navigation state
  useEffect(() => {
    const state = location.state as { email?: string; isReset?: boolean } | null;
    if (state?.email && state?.isReset) {
      setEmail(state.email);
    } else {
      toast({
        title: 'Invalid access',
        description: 'Please request a password reset from the login page.',
        status: 'error',
        duration: 5000,
      });
      navigate('/login');
    }
  }, [location, navigate, toast]);

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!otp || otp.length !== 6) {
      setError('Please enter the 6-digit code from your email.');
      return;
    }

    setIsVerifyingOtp(true);
    setError(null);

    try {
      const { error } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: 'recovery',
      });

      if (error) throw error;
      
      setOtpVerified(true);
      toast({
        title: 'Code verified',
        description: 'Now enter your new password.',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      const authError = error as AuthError;
      setError('Invalid or expired code. Please try again.');
      console.error('OTP verification error:', authError);
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setIsUpdatingPassword(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) throw updateError;

      setSuccess(true);
      toast({
        title: 'Password updated',
        description: 'Your password has been updated successfully.',
        status: 'success',
        duration: 5000,
      });

      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      const authError = error as AuthError;
      setError(authError.message || 'Failed to update password. Please try again.');
      console.error('Password update error:', authError);
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  if (success) {
    return (
      <Container maxW="md" py={10}>
        <Box p={8} borderWidth={1} borderRadius="lg" boxShadow="xl" bg="white">
          <Alert status="success" mb={6}>
            <AlertIcon boxSize="40px" mr={0} />
            <Box ml={4}>
              <AlertTitle>Password Updated!</AlertTitle>
              <AlertDescription>
                Your password has been updated successfully. Redirecting to login...
              </AlertDescription>
            </Box>
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxW="md" py={10}>
      <Box p={8} borderWidth={1} borderRadius="lg" boxShadow="xl" bg="white">
        <Heading as="h1" size="lg" mb={6} textAlign="center">
          Reset Password
        </Heading>

        {error && (
          <Alert status="error" mb={4} borderRadius="md">
            <AlertIcon />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!otpVerified ? (
          <form onSubmit={handleVerifyOtp}>
            <Stack spacing={4}>
              <Text color="gray.600">
                We've sent a 6-digit code to <strong>{email}</strong>
              </Text>

              <FormControl isRequired>
                <FormLabel>Enter 6-digit code</FormLabel>
                <Input
                  type="text"
                  value={otp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setOtp(value);
                  }}
                  placeholder="000000"
                  size="lg"
                  textAlign="center"
                  fontSize="2xl"
                  letterSpacing="0.5em"
                  fontWeight="bold"
                  maxLength={6}
                  autoComplete="one-time-code"
                />
              </FormControl>

              <Button
                type="submit"
                colorScheme="purple"
                size="lg"
                isLoading={isVerifyingOtp}
                loadingText="Verifying..."
                isDisabled={otp.length !== 6}
              >
                Verify Code
              </Button>

              <Button
                variant="link"
                size="sm"
                onClick={() => navigate('/login')}
              >
                Back to Login
              </Button>
            </Stack>
          </form>
        ) : (
          <form onSubmit={handleUpdatePassword}>
            <Stack spacing={4}>
              <FormControl isRequired>
                <FormLabel>New Password</FormLabel>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password"
                  size="lg"
                />
                <Text fontSize="sm" color="gray.500" mt={1}>
                  Must be at least 8 characters
                </Text>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Confirm New Password</FormLabel>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  size="lg"
                />
              </FormControl>

              <Button
                type="submit"
                colorScheme="purple"
                size="lg"
                isLoading={isUpdatingPassword}
                loadingText="Updating..."
                isDisabled={!password || !confirmPassword}
              >
                Update Password
              </Button>
            </Stack>
          </form>
        )}
      </Box>
    </Container>
  );
}

// Export wrapped component
export function UpdatePasswordPage() {
  const [error, setError] = useState<Error | null>(null);

  return (
    <ErrorBoundary
      fallback={
        <PasswordUpdateErrorFallback
          error={error}
          onReset={() => setError(null)}
        />
      }
      onError={(error: Error) => {
        setError(error);
        console.error('UpdatePasswordPage error:', error);
      }}
    >
      <UpdatePasswordPageContent />
    </ErrorBoundary>
  );
}
